import { useEffect, useRef, useCallback } from 'react';
import { useAnnotationRangeDispatch, useAnnotationRangeState } from '../contexts/AnnotationRangeContext';
import { isRangeInsideOfRendererContainer } from './utils';
import { isRoot } from '../../../steps';
import { fg } from '@atlaskit/platform-feature-flags';
export var useUserSelectionRange = function useUserSelectionRange(props) {
  var rendererDOM = props.rendererRef.current;
  var selectionTimeoutRef = useRef();
  var _useAnnotationRangeDi = useAnnotationRangeDispatch(),
    clearRange = _useAnnotationRangeDi.clearRange,
    clearSelectionRange = _useAnnotationRangeDi.clearSelectionRange,
    setSelectionRange = _useAnnotationRangeDi.setSelectionRange;
  var _useAnnotationRangeSt = useAnnotationRangeState(),
    range = _useAnnotationRangeSt.range,
    type = _useAnnotationRangeSt.type,
    selectionDraftRange = _useAnnotationRangeSt.selectionDraftRange;
  var lastRangeRef = useRef(null);
  var onSelectionChange = useCallback(function (event) {
    if (fg('platform_renderer_triple_click_selects_paragraph')) {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(function () {
        var sel = document.getSelection();
        if (!sel || sel.type !== 'Range' || sel.rangeCount !== 1) {
          lastRangeRef.current = null; // Clear last range if selection is invalid
          return;
        }
        var _range = sel.getRangeAt(0);

        // Skip if the selection hasn’t changed
        if (lastRangeRef.current && _range.compareBoundaryPoints(Range.START_TO_START, lastRangeRef.current) === 0 && _range.compareBoundaryPoints(Range.END_TO_END, lastRangeRef.current) === 0) {
          return;
        }
        if (rendererDOM && isRangeInsideOfRendererContainer(rendererDOM, _range)) {
          var _range2 = _range,
            startContainer = _range2.startContainer,
            endContainer = _range2.endContainer;
          var isTripleClick = endContainer.nodeType !== Node.TEXT_NODE;
          if (isTripleClick) {
            var p = startContainer;
            while (p && p.nodeName !== 'P' && p !== rendererDOM) {
              p = p.parentNode;
            }
            if (p && p.nodeName === 'P' && p instanceof Element) {
              var _range3 = document.createRange();
              _range3.selectNodeContents(p);
              sel.removeAllRanges();
              sel.addRange(_range3);
              _range = sel.getRangeAt(0);
            }
          }
          setSelectionRange(_range.cloneRange());
          lastRangeRef.current = _range;
        }
      }, 100);
    } else {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(function () {
        var sel = document.getSelection();
        if (!sel || sel.type !== 'Range' || sel.rangeCount !== 1) {
          return;
        }
        var _range = sel.getRangeAt(0);
        if (rendererDOM && isRangeInsideOfRendererContainer(rendererDOM, _range)) {
          var startContainer = _range.startContainer,
            endContainer = _range.endContainer,
            commonAncestorContainer = _range.commonAncestorContainer;
          var parentNode = startContainer.parentNode;

          // ED-23493
          // On triple-click in Chrome and Safari, the native Selection API's range has endContainer as a non-text node
          // and commonAncestorContainer as root level div.ak-renderer-document when the node is followed by div or hr.

          // Triple clicks are the only case that can cause the endContainer to be a non-text node
          // Same check for highlight range logic in confluence/next/packages/comments-util/src/domUtils.ts Line 180
          var isTripleClick = endContainer.nodeType !== Node.TEXT_NODE;

          // isAnnotationAllowedOnRange range validation is checking if the parent container is root element and disable the comment if it is.
          // platform/packages/editor/renderer/src/steps/index.ts Line 180

          // This workaround ensures the endContainer is set to a text node when endContainer is non-text and the parent container is the root element
          if (isTripleClick && commonAncestorContainer && commonAncestorContainer.nodeType === Node.ELEMENT_NODE &&
          // Ignored via go/ees005
          // eslint-disable-next-line @atlaskit/editor/no-as-casting
          isRoot(commonAncestorContainer) && (parentNode === null || parentNode === void 0 ? void 0 : parentNode.nodeName) === 'P' // ignore if the parent node is strong, em, etc.
          ) {
            var _parentNode$lastChild, _parentNode$lastChild2;
            var lastChild = parentNode !== null && parentNode !== void 0 && parentNode.lastChild && (parentNode === null || parentNode === void 0 || (_parentNode$lastChild = parentNode.lastChild) === null || _parentNode$lastChild === void 0 ? void 0 : _parentNode$lastChild.nodeType) === Node.TEXT_NODE ? parentNode === null || parentNode === void 0 ? void 0 : parentNode.lastChild : parentNode === null || parentNode === void 0 || (_parentNode$lastChild2 = parentNode.lastChild) === null || _parentNode$lastChild2 === void 0 ? void 0 : _parentNode$lastChild2.childNodes[0];
            _range.setEnd(lastChild, lastChild.length || 0);
          }
          setSelectionRange(_range.cloneRange());
        }
      }, 250);
    }
  }, [rendererDOM, setSelectionRange]);
  useEffect(function () {
    if (!document || !rendererDOM) {
      return;
    }
    // Ignored via go/ees005
    // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
    document.addEventListener('selectionchange', onSelectionChange);
    return function () {
      // Ignored via go/ees005
      // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
      document.removeEventListener('selectionchange', onSelectionChange);
      if (fg('platform_renderer_annotation_draft_position_fix')) {
        clearSelectionRange();
      } else {
        clearRange();
      }
    };
  }, [rendererDOM, onSelectionChange, clearSelectionRange, clearRange]);
  return [type, range, selectionDraftRange, fg('platform_renderer_annotation_draft_position_fix') ? clearSelectionRange : clearRange];
};